import React, { useState, useEffect } from 'react';
import { Plus, Download, Eye, Edit, Filter, TrendingDown, Search, Trash2 } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { formatCurrency, formatDate } from '../../lib/utils';
import { expenseService } from '../../lib/database/expenses';
import { CreateExpenseModal } from './CreateExpenseModal';
import { EditExpenseModal } from './EditExpenseModal';
import { ViewExpenseModal } from './ViewExpenseModal';

export const ExpensesList: React.FC = () => {
  const [expenses, setExpenses] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const { data } = await expenseService.getExpenses();
      if (data) {
        setExpenses(data);
        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(expense => expense.category))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleCreateSuccess = () => {
    fetchExpenses();
  };

  const handleEditSuccess = () => {
    fetchExpenses();
    setSelectedExpense(null);
  };

  const handleViewExpense = (expense: any) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const handleEditExpense = (expense: any) => {
    setSelectedExpense(expense);
    setShowEditModal(true);
  };

  const handleEditFromView = () => {
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const handleDeleteClick = (expense: any) => {
    setExpenseToDelete(expense);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!expenseToDelete) return;

    setDeleteLoading(true);
    try {
      await expenseService.deleteExpense(expenseToDelete.id);
      fetchExpenses();
      setShowDeleteDialog(false);
      setExpenseToDelete(null);
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setExpenseToDelete(null);
  };

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-green-100 text-green-700 border-green-200',
      'bg-purple-100 text-purple-700 border-purple-200',
      'bg-orange-100 text-orange-700 border-orange-200',
      'bg-pink-100 text-pink-700 border-pink-200',
      'bg-indigo-100 text-indigo-700 border-indigo-200',
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || expense.category === filter;
    
    return matchesSearch && matchesFilter;
  });

  const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
  const thisMonthExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.date);
    const now = new Date();
    return expenseDate.getMonth() === now.getMonth() && expenseDate.getFullYear() === now.getFullYear();
  }).reduce((sum, expense) => sum + Number(expense.amount), 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600 mt-2">Track and manage business expenses</p>
        </div>
        <Button 
          icon={Plus} 
          className="shadow-lg bg-gradient-to-r from-error-600 to-error-700 hover:from-error-700 hover:to-error-800"
          onClick={() => setShowCreateModal(true)}
        >
          Add Expense
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search expenses, vendors, or categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Category:</span>
            </div>
            <div className="flex space-x-2 flex-wrap">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  filter === 'all'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All
              </button>
              {categories.slice(0, 5).map((category) => (
                <button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    filter === category
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Enhanced Expenses Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Date</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Vendor</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Category</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Amount</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Notes</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900 text-sm uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500 bg-gray-50">
                    {searchTerm || filter !== 'all' ? 'No expenses found matching your criteria.' : 'No expenses recorded yet. Add your first expense!'}
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense, index) => (
                  <tr
                    key={expense.id}
                    className={`transition-all duration-200 hover:bg-red-50 hover:shadow-sm ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="py-4 px-6 border-l-4 border-transparent hover:border-error-400">
                      <p className="font-semibold text-gray-900">{formatDate(expense.date)}</p>
                      <p className="text-sm text-gray-500">
                        Added {formatDate(expense.created_at)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-medium text-gray-900">{expense.vendor}</p>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(expense.category)}`}>
                        {expense.category}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-bold text-lg text-error-600">
                        {formatCurrency(Number(expense.amount))}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-gray-600 text-sm max-w-xs truncate">
                        {expense.notes || 'No notes'}
                      </p>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Eye}
                          onClick={() => handleViewExpense(expense)}
                          className="hover:bg-blue-100"
                        >
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Edit}
                          onClick={() => handleEditExpense(expense)}
                          className="hover:bg-yellow-100"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Trash2}
                          onClick={() => handleDeleteClick(expense)}
                          className="text-error-600 hover:text-error-700 hover:bg-error-50"
                        >
                          Delete
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          icon={Download}
                          className="hover:bg-green-100"
                        >
                          Export
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="sm" className="text-center bg-gradient-to-br from-error-50 to-error-100 border-error-200">
          <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <TrendingDown className="w-6 h-6 text-error-600" />
          </div>
          <p className="text-2xl font-bold text-error-700">{formatCurrency(totalExpenses)}</p>
          <p className="text-sm text-error-600">Total Expenses</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{expenses.length}</p>
          <p className="text-sm text-blue-600">Total Records</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
          <p className="text-2xl font-bold text-warning-700">{formatCurrency(thisMonthExpenses)}</p>
          <p className="text-sm text-warning-600">This Month</p>
        </Card>
        <Card padding="sm" className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <p className="text-2xl font-bold text-purple-700">{categories.length}</p>
          <p className="text-sm text-purple-600">Categories</p>
        </Card>
      </div>

      {/* Modals */}
      <CreateExpenseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditExpenseModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedExpense(null);
        }}
        onSuccess={handleEditSuccess}
        expense={selectedExpense}
      />

      <ViewExpenseModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedExpense(null);
        }}
        onEdit={handleEditFromView}
        expense={selectedExpense}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense from "${expenseToDelete?.vendor}" for ${expenseToDelete ? formatCurrency(Number(expenseToDelete.amount)) : ''}? This action cannot be undone.`}
        confirmText="Delete Expense"
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};